import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { logWarn } from "@/lib/log";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [status, setStatus] = useState("pending");
  const [tries, setTries] = useState(0);
  const { refreshMe } = useAuth();

  useEffect(() => {
    if (!sid) return;
    let cancel = false;
    let attempt = 0;
    const poll = async () => {
      if (cancel) return;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          refreshMe();
          return;
        }
      } catch (err) {
        logWarn("[payment] status poll error", err?.response?.status);
      }
      attempt += 1;
      setTries(attempt);
      if (attempt < 15) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
    return () => { cancel = true; };
  }, [sid, refreshMe]);

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4">
      <div className="border border-[#2A2A2A] bg-[#121212] p-10 max-w-md w-full text-center">
        {status === "pending" && (<>
          <Loader2 className="h-12 w-12 text-[#FF4500] mx-auto mb-4 animate-spin" />
          <div className="font-display text-2xl">Confirming payment...</div>
          <div className="font-mono text-xs text-gray-500 mt-2">Session {sid?.slice(0, 12)}...</div>
        </>)}
        {status === "paid" && (<>
          <CheckCircle2 className="h-12 w-12 text-[#FF4500] mx-auto mb-4" />
          <div className="font-display text-3xl">Crafted.</div>
          <div className="font-mono text-xs text-gray-400 mt-2 uppercase tracking-widest">Your purchase is confirmed</div>
          <div className="mt-6 flex gap-2 justify-center">
            <Link to="/dashboard"><Button data-testid="go-dashboard" className="rounded-none bg-[#FF4500] text-black font-mono text-xs uppercase">Studio</Button></Link>
            <Link to="/browse"><Button data-testid="go-browse" variant="outline" className="rounded-none border-[#2A2A2A] hover:border-[#FF4500] bg-transparent font-mono text-xs uppercase">Keep listening</Button></Link>
          </div>
        </>)}
        {status === "timeout" && (<>
          <XCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <div className="font-display text-2xl">Still processing</div>
          <div className="font-mono text-xs text-gray-400 mt-2">Refresh in a moment.</div>
        </>)}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const nav = useNavigate();
  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4">
      <div className="border border-[#2A2A2A] bg-[#121212] p-10 max-w-md w-full text-center">
        <XCircle className="h-12 w-12 text-gray-500 mx-auto mb-4" />
        <div className="font-display text-2xl">Payment canceled</div>
        <div className="font-mono text-xs text-gray-500 mt-2 uppercase tracking-widest">No charge was made</div>
        <Button onClick={() => nav("/")} className="mt-6 rounded-none bg-[#FF4500] text-black font-mono text-xs uppercase">Back to MeloCraft</Button>
      </div>
    </div>
  );
}
